import * as React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Container,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import MenuIcon from '@mui/icons-material/Menu';
import Person from '@mui/icons-material/Person';
import ThemeToggle from './common/ThemeToggle';
import { useTransition } from '../context/TransitionContext';

/**
 * Navbar component that provides navigation and authentication controls
 * Adapts to user authentication state and screen size
 * Hidden on dashboard routes as dashboard has its own navigation
 * 
 * @returns {JSX.Element | null} The navigation bar or null on dashboard routes
 */
const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const { setTargetRect, isTransitionComplete } = useTransition();
  const titleRef = React.useRef<HTMLAnchorElement>(null);

  React.useLayoutEffect(() => {
    if (titleRef.current) {
      const rect = titleRef.current.getBoundingClientRect();
      setTargetRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    }
  }, [setTargetRect, location.pathname]); // Re-calculate on path change

  // Check if we're on a dashboard route to hide the navbar
  const isDashboard = location.pathname.startsWith('/dashboard');

  /**
   * Opens the user account menu
   * @param event - The mouse event that triggered the menu opening
   */
  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Closes the user account menu
   */
  const handleClose = () => {
    setAnchorEl(null);
  };

  /**
   * Handles user logout process
   * Logs out the user, closes the menu, and navigates to the home page
   */
  const handleLogout = () => {
    logout();
    handleClose();
    navigate('/');
  };

  /**
   * Navigates to the user dashboard/account page
   */
  const handleUserDashboard = () => {
    handleClose();
    navigate('/account');
  };

  /**
   * Navigates to the dashboard page
   * Closes the menu and redirects to the dashboard
   */
  const handleDashboard = () => {
    handleClose();
    navigate('/dashboard');
  };

  /**
   * Navigates to the settings page
   * Closes the menu and redirects to the dashboard settings
   */
  const handleSettings = () => {
    handleClose();
    navigate('/dashboard/profile');
  };

  /**
   * Renders authentication-related buttons based on user authentication status
   * Shows dashboard and user menu for authenticated users, login/register buttons otherwise
   * 
   * @returns {JSX.Element} Authentication UI elements
   */
  const renderAuthButtons = () => {
    if (isAuthenticated) {
      return (
        <>
          <Button
            color="inherit"
            startIcon={<DashboardIcon />}
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 1 }}
          >
            Dashboard
          </Button>
          <Button
            color="inherit"
            startIcon={<Person />}
            onClick={() => navigate('/account')}
            sx={{ mr: 2 }}
          >
            My Account
          </Button>
          <ThemeToggle />
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            color="inherit"
          >
            <AccountCircle />
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem onClick={handleUserDashboard}>
              <Person sx={{ mr: 1 }} /> My Account
            </MenuItem>
            <MenuItem onClick={handleDashboard}>
              <DashboardIcon sx={{ mr: 1 }} /> Dashboard
            </MenuItem>
            <MenuItem onClick={handleSettings}>
              <SettingsIcon sx={{ mr: 1 }} /> Settings
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <LogoutIcon sx={{ mr: 1 }} /> Logout
            </MenuItem>
          </Menu>
        </>
      );
    }

    return (
      <>
        <ThemeToggle />
        <Button color="inherit" onClick={() => navigate('/login')}>
          Login
        </Button>
        <Button color="inherit" onClick={() => navigate('/register')}>
          Register
        </Button>
      </>
    );
  };

  // Don't render the navbar on dashboard routes
  if (isDashboard) {
    return null; // Don't render the navbar in dashboard
  }

  return (
    <AppBar position="static" sx={{
      bgcolor: theme.palette.mode === 'dark' ? '#1b5e20' : 'primary.main',
      zIndex: 1000
    }}>
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Logo/Brand */}
          <Typography
            ref={titleRef}
            variant="h6"
            component={Link}
            to="/"
            sx={{
              flexGrow: 1,
              textDecoration: 'none',
              color: 'inherit',
              fontWeight: 700,
              opacity: isTransitionComplete ? 1 : 0, // Hide until transition completes
              transition: 'opacity 0.3s ease-in',
              '&:hover': {
                color: 'green.100',
              },
            }}
          >
            Farmers HUB
          </Typography>

          {isMobile && (
            <IconButton
              color="inherit"
              onClick={handleDrawerToggle}
              sx={{ ml: 'auto', display: { xs: 'block', md: 'none' } }}
              aria-label="Open navigation menu"
              tabIndex={0}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleDrawerToggle(); }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Navigation Links */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
            <Button color="inherit" component={Link} to="/about">
              About
            </Button>
            <Button color="inherit" component={Link} to="/services">
              Services
            </Button>
            <Button
              color="inherit"
              component={Link}
              to="/agri-dashboard"
              sx={{ fontWeight: 'bold' }}
            >
              County Analysis
            </Button>
            <Button
              color="inherit"
              component={Link}
              to="/import-export"
              sx={{ fontWeight: 'bold' }}
              startIcon={<ImportExportIcon />}
            >
              Import/Export
            </Button>
            <Button
              color="inherit"
              component={Link}
              to="/marketplace"
              sx={{ fontWeight: 'bold' }}
            >
              Marketplace
            </Button>
            <Button color="inherit" component={Link} to="/community" sx={{ fontWeight: 'bold' }}>
              Community
            </Button>
            <Button color="inherit" component={Link} to="/contact">
              Contact
            </Button>
            {renderAuthButtons()}
          </Box>
          <Drawer
            anchor="right"
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{ display: { xs: 'block', md: 'none' } }}
            aria-label="Mobile navigation drawer"
          >
            <Box sx={{ width: 250 }} role="presentation" onClick={handleDrawerToggle} onKeyDown={handleDrawerToggle}>
              <List>
                <ListItemButton component={Link} to="/about">
                  <ListItemText primary="About" />
                </ListItemButton>
                <ListItemButton component={Link} to="/services">
                  <ListItemText primary="Services" />
                </ListItemButton>
                <ListItemButton component={Link} to="/marketplace">
                  <ListItemText primary="Marketplace" />
                </ListItemButton>
                <ListItemButton component={Link} to="/community">
                  <ListItemText primary="Community" />
                </ListItemButton>
                <ListItemButton component={Link} to="/agri-dashboard">
                  <ListItemText primary="County Analysis" />
                </ListItemButton>
                <ListItemButton component={Link} to="/import-export">
                  <ListItemText primary="Import/Export" />
                </ListItemButton>
                <ListItemButton component={Link} to="/dashboard/carbon-credits">
                  <ListItemText primary="Carbon Credits" />
                </ListItemButton>
                <ListItemButton component={Link} to="/contact">
                  <ListItemText primary="Contact" />
                </ListItemButton>
              </List>
              <Box sx={{ mt: 2 }}>{renderAuthButtons()}</Box>
            </Box>
          </Drawer>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar;
